import { inject, Injectable } from '@angular/core';
import { ReportDefinition, SavedReport, ReportVisibility } from '../models/report.model';
import { AuthService } from '../services/auth.service';
import { DatabaseService, type QuerySignal } from './../services/database.service';
import { LoggerService } from '../services/logger.service';

/**
 * CRUD for saved report definitions (Phase 2), stored in the local
 * `report_definitions` table and synced via the existing WebSocket mechanism.
 *
 * Visibility model (decision 2026-07-01): reports default to `private`. Making a
 * report `org_shared` is a separate, explicit action ({@link share}) that the UI
 * guards behind the `reports.share` permission — it is never implied by create
 * or update.
 */
@Injectable({ providedIn: 'root' })
export class SavedReportService {
  private db = inject(DatabaseService);
  private auth = inject(AuthService);
  private log = inject(LoggerService).getLogger('saved-reports');

  private parseRow(row: any): SavedReport {
    const definition: ReportDefinition = row.data
      ? JSON.parse(row.data)
      : { name: row.name, visibility: row.visibility };
    return {
      id: row.id,
      name: row.name || definition.name || '',
      visibility: (row.visibility as ReportVisibility) || 'private',
      definition,
      created_by: row.created_by || '',
      created_at: row.created_at || 0,
      updated_by: row.updated_by || '',
      updated_at: row.updated_at || 0,
    };
  }

  /**
   * Reports the current user may see: their own reports plus any that are
   * shared with the organization, ordered by name.
   */
  async getAllVisible(): Promise<SavedReport[]> {
    await this.db.init();
    const userId = this.auth.getCurrentUserId() ?? '';
    const results = await this.db.exec(
      `SELECT * FROM report_definitions
       WHERE visibility = 'org_shared' OR created_by = ?
       ORDER BY name COLLATE NOCASE`,
      [userId],
    );
    return results.map((row) => this.parseRow(row));
  }

  async getById(id: string): Promise<SavedReport | null> {
    await this.db.init();
    const results = await this.db.exec('SELECT * FROM report_definitions WHERE id = ?', [id]);
    return results.length > 0 ? this.parseRow(results[0]) : null;
  }

  /** Reactive query of all reports visible to the current user. */
  createVisibleQuery(): QuerySignal<SavedReport[]> {
    return this.db.createQuery('report_definitions', () => this.getAllVisible());
  }

  createQueryById(id: string): QuerySignal<SavedReport | null> {
    return this.db.createQuery('report_definitions', () => this.getById(id));
  }

  /** Creates a new saved report. Always starts private unless the definition
   * explicitly opts into org sharing (which the UI only allows with permission). */
  async create(definition: ReportDefinition): Promise<SavedReport> {
    await this.db.init();
    const id = this.db.generateId();
    const userId = this.auth.getCurrentUserId() ?? '';
    const visibility: ReportVisibility = definition.visibility ?? 'private';
    const stored: ReportDefinition = { ...definition, id, visibility };
    const now = Date.now();

    await this.db.run(
      `INSERT INTO report_definitions (id, name, visibility, data, created_by, created_at, updated_by, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      [id, stored.name, visibility, JSON.stringify(stored), userId, now, userId, now],
    );

    const created = await this.getById(id);
    if (!created) throw new Error('Failed to retrieve report after creation');
    this.log.debug('Saved report created', { id, name: stored.name });
    return created;
  }

  /** Updates a report's name/definition. Visibility is preserved — use
   * {@link share}/{@link unshare} to change it. */
  async update(id: string, definition: ReportDefinition): Promise<SavedReport> {
    await this.db.init();
    const existing = await this.getById(id);
    if (!existing) throw new Error(`Report '${id}' not found`);

    const userId = this.auth.getCurrentUserId() ?? '';
    const stored: ReportDefinition = {
      ...definition,
      id,
      visibility: existing.visibility, // preserve; sharing is a separate action
    };
    const now = Date.now();

    await this.db.run(
      `UPDATE report_definitions SET name = ?, data = ?, updated_by = ?, updated_at = ? WHERE id = ?`,
      [stored.name, JSON.stringify(stored), userId, now, id],
    );

    const updated = await this.getById(id);
    if (!updated) throw new Error('Failed to retrieve report after update');
    return updated;
  }

  async remove(id: string): Promise<void> {
    await this.db.init();
    await this.db.run('DELETE FROM report_definitions WHERE id = ?', [id]);
    this.log.debug('Saved report deleted', { id });
  }

  /** Shares a report with the organization. UI guards this behind reports.share. */
  async share(id: string): Promise<void> {
    await this.setVisibility(id, 'org_shared');
  }

  /** Reverts a report to private. */
  async unshare(id: string): Promise<void> {
    await this.setVisibility(id, 'private');
  }

  private async setVisibility(id: string, visibility: ReportVisibility): Promise<void> {
    await this.db.init();
    const existing = await this.getById(id);
    if (!existing) throw new Error(`Report '${id}' not found`);

    const userId = this.auth.getCurrentUserId() ?? '';
    const stored: ReportDefinition = { ...existing.definition, id, visibility };
    await this.db.run(
      `UPDATE report_definitions SET visibility = ?, data = ?, updated_by = ?, updated_at = ? WHERE id = ?`,
      [visibility, JSON.stringify(stored), userId, Date.now(), id],
    );
    this.log.debug('Saved report visibility changed', { id, visibility });
  }
}
